import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import ComboLocationSelect, { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { Switch } from '@components/Switch';
import { useTranslation, useLocations } from '@hooks';

export const channelLinkPayload = values => ({
    locationId:
        values.channelLinkEnabled && values.locationType !== LOCATION_TYPE.BRAND ? values.locationId || null : null,
    brandId: values.channelLinkEnabled && values.locationType === LOCATION_TYPE.BRAND ? values.brandId || null : null,
});

const ChannelLocationSelect = ({ data }) => {
    const { control, setValue, watch, trigger } = useFormContext();
    const { t } = useTranslation();
    const { getSelectLocationsOptions, hasSingleLocation } = useLocations();
    const brands = useSelector(state => state.brands.data);
    const locations = useSelector(state => state.account?.account?.companies || []);

    const assignEnabled = watch('channelLinkEnabled');
    const channelLinkAlreadySetToForm = watch('channelLinkAlreadySetToForm');

    useEffect(() => {
        if (hasSingleLocation) {
            setValue('channelLinkEnabled', true);
            setValue('channelLinkAlreadySetToForm', true);
            return;
        }
        if (data && !channelLinkAlreadySetToForm) {
            setValue('channelLinkEnabled', !!(data.locationId || data.brandId));
            setValue('channelLinkAlreadySetToForm', true);
        }
    }, [data, channelLinkAlreadySetToForm, setValue, hasSingleLocation]);

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);
    const locationsOptions = useMemo(() => getSelectLocationsOptions(), [locations]);

    const comboLocationSelect = (
        <ComboLocationSelect
            brandsOptions={brandsOptions}
            locationsOptions={locationsOptions}
            data={data}
            updatedLocationTypeCb={() => trigger(['locationId', 'brandId'])}
        />
    );

    // ComboLocationSelect auto-selects the single location and renders nothing, so the
    // channel stays linked without the toggle, which would be a dead end here.
    if (hasSingleLocation) {
        return comboLocationSelect;
    }

    return (
        <div style={{ gridColumn: '1 / -1' }}>
            <Controller
                control={control}
                name="channelLinkEnabled"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={!!field.value}
                        handleChange={event => {
                            setValue('channelLinkEnabled', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            trigger(['locationId', 'brandId']);
                        }}
                        label={t('SettingsChannels.linkChannelToLocation')}
                    />
                )}
            />
            {assignEnabled && comboLocationSelect}
        </div>
    );
};

export default ChannelLocationSelect;
